// Metric cards on Overview (e.g. Majlis's "Calls"): a project's stats over its own events, from
// /api/metric_cards, filled in after the rest of the screen. Nothing shows for projects without
// cards. Settings → Site settings has the admin editor for the definitions (metricCardsEditor).
import type { MetricCardsConfig, MetricCardsView, MetricCardView, MetricFormatView, MetricStatView } from "../src/metric-cards-types";
import { areaChart, deltaPill } from "./chart";
import { shared } from "./share";

type Api = <T>(path: string, p?: Record<string, string>) => Promise<T>;

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dayLabel = (s: string) => `${Number(s.slice(8, 10))} ${MON[Number(s.slice(5, 7)) - 1]}`;
const cap = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

/** "45s", "3m 05s", "12h 04m"; totals past 100 hours drop the minutes. */
export function fmtDuration(sec: number): string {
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
  const h = Math.floor(s / 3600);
  return h >= 100 ? `${h.toLocaleString("en-US")}h` : `${h}h ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}m`;
}

/** A metric value in its format; "–" when there is none. Small fractional numbers keep one decimal (2.4 participants). */
export function fmtMetricValue(v: number | null | undefined, f: MetricFormatView): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "–";
  if (f === "duration_s") return fmtDuration(v);
  if (f === "percent") return `${(v * 100).toFixed(1)}%`;
  if (Math.abs(v) >= 10_000) return `${(v / 1000).toFixed(1)}k`;
  if (!Number.isInteger(v) && Math.abs(v) < 100) return v.toFixed(1);
  return Math.round(v).toLocaleString("en-US");
}

const SPLIT_COLORS = ["#2F5BD3", "#16233D", "#A9B8E6", "#7C8BB0", "#CBD2DF", "#5B6B8C"];

function tile(m: MetricStatView): string {
  if (m.kind === "split") {
    const parts = m.parts ?? [];
    const total = parts.reduce((s, p) => s + p.count, 0);
    const pct = (n: number) => (total ? Math.round((n / total) * 100) : 0);
    const top = [...parts].sort((a, b) => b.count - a.count)[0];
    const bar = total
      ? `<div class="stackbar mc-split" role="img" aria-label="${esc(parts.map((p) => `${cap(p.value)} ${pct(p.count)}%`).join(", "))}">${parts
          .filter((p) => p.count > 0)
          .map((p) => `<div style="width:${(p.count / total) * 100}%;background:${SPLIT_COLORS[parts.indexOf(p) % SPLIT_COLORS.length]}"></div>`)
          .join("")}</div>`
      : "";
    const legend = parts
      .map((p, i) => `<span><i style="background:${SPLIT_COLORS[i % SPLIT_COLORS.length]}"></i>${esc(cap(p.value))} ${total ? `${pct(p.count)}%` : "–"}</span>`)
      .join("");
    return `<div class="rev-kpi mc-kpi"><span class="label">${esc(m.label)}</span><span class="value num">${top && total ? `${pct(top.count)}% ${esc(top.value)}` : "–"}</span>${bar}<span class="foot mc-legend">${legend}</span></div>`;
  }
  const value = fmtMetricValue(m.value, m.format);
  return `<div class="rev-kpi mc-kpi"><span class="label">${esc(m.label)}</span><span class="value num" title="${esc(value)}">${value}</span><span class="foot">${deltaPill(m.value, m.previous, m.invert)}</span></div>`;
}

/** No events at all for the card in the range: every count is 0 and nothing else has a value. */
const isEmpty = (c: MetricCardView) => c.metrics.every((m) => !m.value);

function cardHtml(c: MetricCardView, v: MetricCardsView, i: number): string {
  const head = `<div class="rev-head"><h2>${esc(c.title)}</h2><span class="spacer"></span><span class="rev-from">${dayLabel(v.range.from)} – ${dayLabel(v.range.to)}</span></div>`;
  if (!v.available)
    return `<section class="card flush mc">${head}<div class="mc-msg">These numbers are read from the live store, which keeps the last 90 days. Pick a range inside the last 90 days to see them.</div></section>`;
  if (c.error) return `<section class="card flush mc">${head}<div class="mc-msg">Couldn't load this card. Try again in a minute.</div></section>`;
  if (isEmpty(c)) return `<section class="card flush mc">${head}<div class="mc-msg">No events for this card in this range yet.</div></section>`;
  const trend = c.metrics.find((m) => m.daily?.length);
  return `<section class="card flush mc">${head}
    <div class="rev-kpis mc-kpis">${c.metrics.map(tile).join("")}</div>
    ${
      trend
        ? `<div class="chart-wrap mc-trend"><div class="chart-head"><h2>${esc(trend.label)} per day</h2></div><div id="mc-chart-${i}"></div></div>`
        : ""
    }
  </section>`;
}

/** The slot Overview leaves for the cards; empty until renderMetricCards fills it. */
export const metricCardsSlot = () => (shared ? "" : `<div id="metric-cards" class="mc-slot"></div>`);

export async function renderMetricCards(o: { api: Api; params: Record<string, string>; mobile: boolean; current: () => boolean }): Promise<void> {
  const host = document.getElementById("metric-cards");
  if (!host || shared) return;
  let v: MetricCardsView;
  try {
    v = await o.api<MetricCardsView>("metric_cards", o.params);
  } catch {
    return; // the rest of Overview is still useful
  }
  if (!o.current() || !v.cards.length) return;
  host.innerHTML = v.cards.map((c, i) => cardHtml(c, v, i)).join("");
  v.cards.forEach((c, i) => {
    const el = document.getElementById(`mc-chart-${i}`);
    const m = c.metrics.find((x) => x.daily?.length);
    if (!el || !m) return;
    areaChart(el, {
      labels: m.daily!.map((d) => dayLabel(d.day)),
      current: m.daily!.map((d) => d.value),
      metric: m.label,
      format: (x) => fmtMetricValue(x, m.format),
      compact: true,
    });
  });
}

// ---------- Settings → Site settings (admins) ----------

const SAVE_ERRORS: Record<string, string> = {
  bad_json: "That isn't valid JSON.",
  bad_cards: "Expected a list of at most 6 cards.",
  bad_card: "Each card needs a title and a list of metrics.",
  bad_title: "Each card needs a title (up to 60 characters).",
  duplicate_title: "Two cards have the same title.",
  bad_metrics: "Each card needs 1 to 12 metrics.",
  bad_metric: "A metric isn't an object.",
  bad_label: "Each metric needs a label (up to 60 characters).",
  bad_event: "An event name is missing or has unusual characters.",
  bad_prop: "A property name is missing or has characters other than letters, digits, _ and -.",
  bad_aggregate: "Aggregate must be count, sum, avg, median, p90 or max (ratios: count or sum).",
  bad_format: "Format must be number, duration_s or percent.",
  bad_filter: "A filter needs a prop and either equals or gt.",
  bad_kind: "Kind must be stat (the default), ratio or split.",
  bad_values: "A split needs 1 to 6 values.",
  card_too_complex: "That card reads too many properties for one query. Split it into two cards.",
  not_allowed: "Only MonoLog admins can change metric cards.",
};

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
  return out as T;
}

/** A JSON editor for the site's cards: the stored definitions, saved whole. */
export async function metricCardsEditor(el: HTMLElement, site: { id: string; name: string }, current: () => boolean): Promise<void> {
  el.innerHTML = `<div class="card"><div class="card-head"><h2>Metric cards</h2></div><div class="acct-msg">Loading…</div></div>`;
  let cfg: MetricCardsConfig;
  try {
    cfg = await call<MetricCardsConfig>("GET", `metric_cards/config?project=${encodeURIComponent(site.id)}`);
  } catch (err) {
    if (current()) el.innerHTML = `<div class="card"><div class="card-head"><h2>Metric cards</h2></div><div class="acct-msg error">Couldn't load metric cards (${esc((err as Error).message)}).</div></div>`;
    return;
  }
  if (!current()) return;
  el.innerHTML = `<div class="card">
    <div class="card-head"><h2>Metric cards</h2><span class="spacer"></span><span class="hint">Admins only</span></div>
    <p class="muted usr-intro">Stat cards on ${esc(site.name)}'s Overview, over its own events (last 90 days). A list of cards, each <span class="mono">{"title", "metrics": [...]}</span>. A metric is <span class="mono">{"label", "event", "aggregate": "count" | "sum" | "avg" | "median" | "p90" | "max", "prop", "format": "number" | "duration_s" | "percent", "filter": {"prop", "equals" | "gt"}, "trend": true}</span>, a ratio <span class="mono">{"kind": "ratio", "label", "numerator": {"event"}, "denominator": {"event"}}</span> or a split <span class="mono">{"kind": "split", "label", "event", "prop", "values": [...]}</span>. Save an empty list <span class="mono">[]</span> to remove them.</p>
    <form class="mc-edit">
      <textarea class="acct-input mc-json mono" name="json" spellcheck="false" aria-label="Metric cards JSON" rows="14">${esc(JSON.stringify(cfg.cards, null, 2))}</textarea>
      <div class="usr-actions"><button class="btn primary" type="submit">Save</button><span class="acct-msg" role="status"></span></div>
    </form>
  </div>`;
  const form = el.querySelector<HTMLFormElement>(".mc-edit")!;
  const msg = form.querySelector<HTMLElement>("[role=status]")!;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const text = form.querySelector<HTMLTextAreaElement>("textarea")!.value;
    let cards: unknown;
    try {
      cards = JSON.parse(text);
    } catch {
      msg.textContent = SAVE_ERRORS.bad_json!;
      msg.className = "acct-msg error";
      return;
    }
    try {
      const saved = await call<MetricCardsConfig>("PUT", "metric_cards/config", { project: site.id, cards });
      form.querySelector<HTMLTextAreaElement>("textarea")!.value = JSON.stringify(saved.cards, null, 2);
      msg.textContent = saved.cards.length ? "Saved." : "Saved: no cards.";
      msg.className = "acct-msg";
    } catch (err) {
      const code = (err as Error).message;
      msg.textContent = SAVE_ERRORS[code] ?? `Couldn't save (${code}).`;
      msg.className = "acct-msg error";
    }
  });
}
